import {defineStore} from "pinia";
import {useMenusStore} from "@/new-menu/stores";
import {useCurrencyStore} from "@/shared/stores";
import {hexToRgb, isTextColorDark} from "@/shared/helpers/ui";

const baseUrl = "https://img.qromo.io/fonts/";
const pdfParameters = {
	fzTitle: 24,
	fzSubtitle: 16,
	fzText: 12,
	paddingXLight: 3,
	paddingLight: 6,
	paddingMedium: 9,
	paddingHeavy: 15,
	columnWidth: 40,
	columnWidthPrice: 30,
}

export const usePdfStore = defineStore('pdf', {
	state: () => ({
		currentPdf: null,
		currentPdfBlob: null,
		loadingPdf: false,
		currentMenuId: null,
		currentTheme: null,
		themeColors: null,
		imgLogo: null,
		hidePrices: false,
		currentFontIndex: 0,
	}),
	getters: {
		currentFont: state => {
			return fonts[state.currentFontIndex];
		},
		fontsName: state => {
			return fonts.map(f => f.name);
		},
		doesCurrentFontSupportBold: state => {
			return state.currentFont.weight.hasOwnProperty('700');
		}
	},
	actions: {
		getFonts() {
			return fonts;
		},
		fetchLogo(callback) {
			const menusStore = useMenusStore();
			if(menusStore.business.logo) {
				fetch(menusStore.business.logo)
					.then(response => {
						if(response.ok) {
							return response.blob();
						} else {
							throw new Error('Network response was not ok.');
						}
					}
					).then(blob => {
						let reader = new FileReader();
						reader.onload = () => {
							this.imgLogo = reader.result;
							callback();
						}
						reader.readAsDataURL(blob);
					}
					).catch(error => {
						console.error('There has been a problem with your fetch operation:', error);
						callback();
					});

			} else {
				callback();
			}
		},
		fetchFont(callback) {
			const font = this.currentFont;
			let count = 0;
			Object.entries(font.weight).forEach(([weight, fontData]) => {
				if(font.weight[weight].hasOwnProperty('data') || font.weight[weight].data) {
					return;
				}
				let fontUrl = baseUrl + fontData.name;
				count++;
				fetch(fontUrl).then(response => {
					if(response.ok) {
						return response.arrayBuffer();
					} else {
						throw new Error('Network response was not ok.');
					}
				}).then(arrayBuffer => {
					let binaryString = '';
					let bytes = new Uint8Array(arrayBuffer);
					bytes.forEach((byte) => {
						binaryString += String.fromCharCode(byte);
					});
					font.weight[weight].data = binaryString;
					count--;
					if(count === 0) {
						callback();
					}
				}).catch(error => {
					console.error('There has been a problem with your fetch operation:', error);
				});
			});
			if(count === 0) {
				callback();
			}
		},
		generatePdf() {
			if(window.jspdf) {

				const menusStore = useMenusStore();

				let menu = null;
				if(this.currentMenuId !== null) {
					menusStore.menus.forEach(m => {
						if(this.currentMenuId && this.currentMenuId === m.menu_id) {
							menu = m;
						}
					});
				} else if(menusStore.menus.length > 0) {
					menu = menusStore.menus[0];
					this.currentMenuId = menu.menu_id;
				}
				if(menu === null) {
					console.error('Menu not found');
					return;
				}

				this.loadingPdf = true;

				this.fetchLogo(() => {
					this.fetchFont(() => {
						const { jsPDF } = window.jspdf;
						const doc = new jsPDF();

						Object.entries(this.currentFont.weight).forEach(([weight, fontData]) => {
							doc.addFileToVFS(fontData.name, this.currentFont.weight[weight].data);
							doc.addFont(fontData.name, this.currentFont.name, fontData.style);
							doc.setFont(this.currentFont.name);
						});

						this.setThemeColors();
						this.writePdf(doc, menu);
						this.currentPdfBlob = doc.output('blob');
						this.currentPdf = doc.output('datauristring');

						this.loadingPdf = false;
					});
				});
			} else {
				console.error('jsPDF not found');
			}
		},
		writePdf(doc, menu) {

			let x = pdfParameters.columnWidth + pdfParameters.paddingMedium;
			let y = pdfParameters.paddingHeavy;

			this.drawBackground(doc);
			this.drawLogo(doc);
			({x, y} = this.writeMenu(doc, menu, x, y+2));


		},
		writeMenu(doc, menu, x, y) {
			if(!isStringEmpty(menu.name)) {
				({x, y} = this.writeText(doc, menu.nameTranslation, x, y, pdfParameters.fzTitle, pdfParameters.paddingXLight, null, null, true));
			}

			const menusStore = useMenusStore();
			if(menusStore.business.description) {
				({x, y} = this.writeText(doc, menusStore.business.descriptionTranslation, x, y, pdfParameters.fzText, pdfParameters.paddingHeavy, this.themeColors.txt_2));
			}

			menu.categories.forEach(category => {
				({x, y} = this.writeCategory(doc, category, x, y));
			});

			return {x, y};
		},
		writeCategory(doc, category, x, y) {
			if(isStringEmpty(category.name)) {
				return {x, y};
			}
			if(category.items.length === 0) {
				return {x, y};
			}
			const menusStore = useMenusStore();
			let categoryColor = category.color || menusStore.business.color || '#0752A3';
			let textColor = isTextColorDark(categoryColor) ? [0,0,0] : [255,255,255];
			let [r,g,b] = hexToRgb(categoryColor);

			({x, y} = this.writeText(doc, category.nameTranslation, x, y, pdfParameters.fzSubtitle, pdfParameters.paddingMedium, textColor, (preX, preY, textDimensionsW, textDimensionsH) => {
				doc.saveGraphicsState();
				doc.setFillColor(r, g, b);
				doc.setGState(new doc.GState({opacity: 0.75}));
				let width = textDimensionsW + 14 + pdfParameters.paddingLight;
				doc.rect(preX - 14, preY - pdfParameters.paddingLight, width, textDimensionsH + 3, 'F');
				doc.restoreGraphicsState();
			}, true));


			category.items.forEach(item => {
				({x, y} = this.writeItem(doc, item, x, y));
			});

			y += pdfParameters.paddingHeavy + pdfParameters.paddingXLight;

			return {x, y};
		},
		writeItem(doc, item, x, y) {
			if(isStringEmpty(item.name)) {
				return {x, y};
			}

			({x, y} = this.writeText(doc, item.nameTranslation, x, y, pdfParameters.fzText, 0, null, (xPre, yPre) => {

				if(item.price && !this.hidePrices) {
					let price = useCurrencyStore().readableCurrencyValue(item.price);
					let xPos = doc.internal.pageSize.width - pdfParameters.paddingMedium;
					doc.setFontSize(pdfParameters.fzText);
					doc.setTextColor(this.themeColors.txt_1[0], this.themeColors.txt_1[1], this.themeColors.txt_1[2]);
					doc.text(price, xPos, yPre, {
						align: 'right',
						maxWidth: pdfParameters.columnWidthPrice,
					});
				}
			}));


			if(item.ingredients) {
				y += pdfParameters.paddingXLight;
				({x, y} = this.writeText(doc, item.ingredientsFull ? item.ingredientsFullTranslation : item.ingredientsTranslation, x, y, pdfParameters.fzText, pdfParameters.paddingLight, this.themeColors.txt_2));
			} else {
				y += pdfParameters.paddingLight;
			}
			return {x, y};
		},
		writeText(doc, text, x, y, fontSize, padding, color = null, beforeDraw = null, bold = false) {
			doc.setFontSize(fontSize);

			const pageHeight = doc.internal.pageSize.height;
			const pageWidth = doc.internal.pageSize.width;

			let textUsed = this.removeNonPrintableChars(text || "");

			const textDimensions = doc.getTextDimensions(textUsed, {
				fontSize: fontSize,
				fontStyle: bold && this.doesCurrentFontSupportBold ? 'bold' : 'normal',
				maxWidth: pageWidth - pdfParameters.columnWidth - pdfParameters.columnWidthPrice - padding * 2,
			});
			const textHeight = textDimensions.h;

			if (y + textHeight > pageHeight - pdfParameters.paddingHeavy) {
				doc.addPage();
				this.drawBackground(doc);
				this.drawLogo(doc);
				y = pdfParameters.paddingHeavy;
			}

			beforeDraw && beforeDraw(x, y, textDimensions.w, textDimensions.h);

			if(color) {
				doc.setTextColor(color[0], color[1], color[2]);
			} else {
				doc.setTextColor(this.themeColors.txt_1[0], this.themeColors.txt_1[1], this.themeColors.txt_1[2]);
			}
			doc.setFont(this.currentFont.name, bold && this.doesCurrentFontSupportBold ? 'bold' : 'normal');
			doc.text(textUsed, x, y, {
				maxWidth: pageWidth - pdfParameters.columnWidth - pdfParameters.columnWidthPrice - padding * 2,
			});
			doc.setFont(this.currentFont.name, 'normal');

			return {
				x: x,
				y: y + textHeight + padding,
				textWidth: textDimensions.w,
				textHeight: textDimensions.h,
			}
		},
		drawBackground(doc) {

			doc.setFillColor(this.themeColors.bg_1[0], this.themeColors.bg_1[1], this.themeColors.bg_1[2]);
			doc.rect(0, 0, pdfParameters.columnWidth, doc.internal.pageSize.height, 'F');
			doc.setFillColor(this.themeColors.bg_2[0], this.themeColors.bg_2[1], this.themeColors.bg_2[2]);
			doc.rect(pdfParameters.columnWidth, 0, doc.internal.pageSize.width - pdfParameters.columnWidth, doc.internal.pageSize.height, 'F');
		},
		drawLogo(doc) {
			if(this.imgLogo) {
				const imgWidth = 18;
				const imgHeight = 18;
				const imgX = pdfParameters.columnWidth / 2 - imgWidth / 2;
				const imgY = pdfParameters.paddingMedium;
				doc.addImage(this.imgLogo, 'PNG', imgX, imgY, imgWidth, imgHeight);
			}
		},
		removeNonPrintableChars(text) {
			return text.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '');
		},
		setThemeColors() {

			const menusStore = useMenusStore();

			if(this.currentTheme) {
				let [r1,g1,b1] = hexToRgb(this.currentTheme.bg_1);
				let [r2,g2,b2] = hexToRgb(this.currentTheme.bg_2);
				let [r3,g3,b3] = hexToRgb(this.currentTheme.txt_1);
				let [r4,g4,b4] = hexToRgb(this.currentTheme.txt_2);

				this.themeColors = {
					bg_1: [r1, g1, b1],
					bg_2: [r2, g2, b2],
					txt_1: [r3, g3, b3],
					txt_2: [r4, g4, b4],
				}
			} else if(menusStore.theme) {
				let [r1,g1,b1] = hexToRgb(menusStore.theme.bg_1);
				let [r2,g2,b2] = hexToRgb(menusStore.theme.bg_2);
				let [r3,g3,b3] = hexToRgb(menusStore.theme.txt_1);
				let [r4,g4,b4] = hexToRgb(menusStore.theme.txt_2);

				this.themeColors = {
					bg_1: [r1, g1, b1],
					bg_2: [r2, g2, b2],
					txt_1: [r3, g3, b3],
					txt_2: [r4, g4, b4],
				}
			} else {
				this.themeColors = {
					bg_1: [255, 255, 255],
					bg_2: [255, 255, 255],
					txt_1: [0, 0, 0],
					txt_2: [0, 0, 0],
				}
			}
		}
	}
});

function isStringEmpty(str) {
	return (!str || 0 === str.length);
}

const fonts = [
	{
		name: 'Montserrat',
		weight: {
			"400": {
				name: "Montserrat-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Montserrat-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'OpenSans',
		weight: {
			"400": {
				name: "OpenSans-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "OpenSans-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Quicksand',
		weight: {
			"400": {
				name: "Quicksand-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Quicksand-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Raleway',
		weight: {
			"400": {
				name: "Raleway-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Raleway-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'NunitoSans',
		weight: {
			"400": {
				name: "NunitoSans-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "NunitoSans-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Poppins',
		weight: {
			"400": {
				name: "Poppins-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Poppins-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Roboto',
		weight: {
			"400": {
				name: "Roboto-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Roboto-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Lato',
		weight: {
			"400": {
				name: "Lato-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Lato-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Ubuntu',
		weight: {
			"400": {
				name: "Ubuntu-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Ubuntu-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Merriweather',
		weight: {
			"400": {
				name: "Merriweather-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Merriweather-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Bitter',
		weight: {
			"400": {
				name: "Bitter-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Bitter-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Arvo',
		weight: {
			"400": {
				name: "Arvo-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Arvo-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'LibreCaslonText',
		weight: {
			"400": {
				name: "LibreCaslonText-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "LibreCaslonText-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'PlayfairDisplay',
		weight: {
			"400": {
				name: "PlayfairDisplay-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "PlayfairDisplay-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'AveriaSerifLibre',
		weight: {
			"400": {
				name: "AveriaSerifLibre-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "AveriaSerifLibre-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'FiraMono',
		weight: {
			"400": {
				name: "FiraMono-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "FiraMono-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Inconsolata',
		weight: {
			"400": {
				name: "Inconsolata-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "Inconsolata-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'Lobster',
		weight: {
			"400": {
				name: "Lobster-Regular.ttf",
				style: "normal",
			}
		}
	},
	{
		name: 'MontaguSlab',
		weight: {
			"400": {
				name: "MontaguSlab-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "MontaguSlab-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'PlaypenSans',
		weight: {
			"400": {
				name: "PlaypenSans-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "PlaypenSans-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'RedHatDisplay',
		weight: {
			"400": {
				name: "RedHatDisplay-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "RedHatDisplay-Bold.ttf",
				style: "bold",
			}
		}
	},
	{
		name: 'ShantellSans',
		weight: {
			"400": {
				name: "ShantellSans-Regular.ttf",
				style: "normal",
			},
			"700": {
				name: "ShantellSans-Bold.ttf",
				style: "bold",
			}
		}
	},
]